// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useEffect, useState } from 'react';
import {
  DefaultButton,
  Dropdown,
  IModalStyles,
  ITextStyles,
  Label,
  Modal,
  Spinner,
  Stack,
  Text
} from '@fluentui/react';
import { Dialpad } from '@azure/communication-react';
import { JoinCallButton, JoinCallIdentityKind } from './JoinCallButton';

export type PSTNCallInfo = {
  numberToCall: string;
  alternateCallerId: string;
  joinIdentity: JoinCallIdentityKind;
};

export const PSTNCallModal = (props: {
  isOpen: boolean;
  onCancel: () => void;
  onStartCall: (callInfo: PSTNCallInfo) => Promise<void>;
  functionsAppToken: string | undefined;
}): JSX.Element => {
  const [numberToCall, setNumberToCall] = useState<string>();
  const [alternateCallerId, setAlternateCallerId] = useState<string>();
  const [availableCallerIds, setAvailableCallerIds] = useState<string[]>();
  const [noAvailableCallerIds, setNoAvailableCallerIds] = useState<boolean>(false);
  useEffect(() => {
    if (!props.isOpen || availableCallerIds || !props.functionsAppToken) {
      return;
    }

    fetch('api/requestPSTNNumber', {
      headers: new Headers({
        Authorization: `Bearer ${props.functionsAppToken}`
      })
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data || !data.phoneNumbers || data.phoneNumbers.length === 0) {
          setNoAvailableCallerIds(true);
        }
        setAvailableCallerIds(data.phoneNumbers.map((phoneNumber) => phoneNumber.phoneNumber));
        setAlternateCallerId(data.phoneNumbers[0].phoneNumber);
      });
  }, [props.isOpen, availableCallerIds, props.functionsAppToken]);

  const startCallClicked = async (joinIdentity: JoinCallIdentityKind): Promise<void> => {
    if (!numberToCall) {
      throw new Error('Invalid number');
    }
    if (!alternateCallerId) {
      throw new Error('Invalid alternate caller id');
    }
    await props.onStartCall({ numberToCall, alternateCallerId, joinIdentity });
  };

  return (
    <Modal isOpen={props.isOpen} styles={modalStyles} onDismiss={props.onCancel}>
      <Stack tokens={{ childrenGap: '2rem' }}>
        <Stack.Item>
          <Text styles={headingTestStyles}>Make a PSTN Call</Text>
        </Stack.Item>
        <Stack.Item styles={{ root: { margin: '0 1.5rem' } }}>
          <Label required>Your Caller ID</Label>
          {noAvailableCallerIds && <Text>No available caller IDs found</Text>}
          {!availableCallerIds && <Spinner label="Fetching available caller IDs" />}
          {availableCallerIds && (
            <Dropdown
              placeholder="Select a number"
              options={availableCallerIds.map((callerId) => ({ key: callerId, text: callerId }))}
              onChange={(_, option) => setAlternateCallerId(option?.key as string)}
              selectedKey={alternateCallerId}
              styles={{ dropdown: { width: '100%' } }}
            />
          )}
        </Stack.Item>
        <Stack.Item>
          <Stack styles={{ root: { margin: '0 1.5rem' } }}>
            <Label required>Callee number</Label>
            <Text variant="small" styles={{ root: { maxWidth: '100%', fontWeight: 'bold', marginBottom: '0.5rem' } }}>
              You must include international code (e.g. +1)
            </Text>
            <Dialpad
              onChange={(newNumber) => setNumberToCall(newNumber)}
              showDeleteButton={true}
              styles={{ root: { padding: 0, margin: 0 } }}
            />
          </Stack>
        </Stack.Item>
        <Stack.Item align="end">
          <Stack horizontal tokens={{ childrenGap: '1rem' }}>
            <Stack.Item>
              <JoinCallButton
                disabled={!numberToCall || !alternateCallerId}
                text={'Start call'}
                iconName={'PSTNOutboundCall'}
                onJoinClick={startCallClicked}
                spinnerAriaLabel="Starting PSTN Call"
              />
            </Stack.Item>
            <Stack.Item>
              <DefaultButton text="Cancel" onClick={props.onCancel} />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </Modal>
  );
};

const modalStyles: Partial<IModalStyles> = {
  main: {
    padding: '2rem'
  }
};

const headingTestStyles: ITextStyles = {
  root: {
    fontWeight: '600',
    fontSize: '1.5rem'
  }
};
